import React from "react";
import { Shield } from "lucide-react";

const LAST_UPDATED = "1 April 2026";
const COMPANY = "FindJoy143";
const EMAIL = "findjoy143@gmail.com";

export default function PrivacyPolicy() {
  return (
    <div className="max-w-3xl animate-fade-in" style={{ fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif" }}>
      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 bg-blue-50 rounded-lg flex items-center justify-center">
          <Shield size={20} className="text-blue-700" />
        </div>
        <div>
          <h1 className="text-2xl font-black text-gray-900 tracking-tight">Privacy Policy</h1>
          <p className="text-sm text-gray-500">Last updated: {LAST_UPDATED}</p>
        </div>
      </div>

      <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-6">
        <p className="text-sm text-amber-800 font-bold mb-1">Summary (plain English)</p>
        <ul className="text-sm text-amber-700 space-y-1">
          <li>• We do not sell your data to anyone, ever</li>
          <li>• Your financial data is stored only on our encrypted servers</li>
          <li>• We do not share your information with advertisers</li>
          <li>• You can delete your account and all data at any time</li>
          <li>• We only collect what we need to provide the service</li>
        </ul>
      </div>

      <div className="bg-white border border-gray-100 rounded-xl p-6 space-y-6 text-sm text-gray-700 leading-relaxed">
        <section>
          <h2 className="font-bold text-gray-900 text-base mb-3">1. Who We Are</h2>
          <p>{COMPANY} ("TaxBuddy", "we", "us", "our") operates the TaxBuddy platform, a financial record-keeping tool to help Australian residents organise their expenses for tax purposes. We are bound by the <em>Privacy Act 1988</em> (Cth) and the Australian Privacy Principles (APPs).</p>
          <p className="mt-2">Contact: <a href={`mailto:${EMAIL}`} className="text-blue-700 underline">{EMAIL}</a></p>
        </section>

        <section>
          <h2 className="font-bold text-gray-900 text-base mb-3">2. What Information We Collect</h2>
          <p className="font-semibold text-gray-800 mb-2">Information you provide directly:</p>
          <ul className="list-disc pl-5 space-y-1">
            <li>Your name and email address when you register</li>
            <li>Bank statement PDFs you upload for processing</li>
            <li>Transaction categories and notes you add manually</li>
            <li>Settings and preferences you configure</li>
          </ul>
          <p className="font-semibold text-gray-800 mt-4 mb-2">Information collected automatically:</p>
          <ul className="list-disc pl-5 space-y-1">
            <li>Log data (IP address, browser type, pages visited) for security and debugging</li>
            <li>Session tokens for authentication</li>
          </ul>
          <p className="mt-3 font-semibold text-red-700">What we do NOT collect:</p>
          <ul className="list-disc pl-5 space-y-1 text-red-700">
            <li>We do not store your online banking credentials or passwords</li>
            <li>We do not access your bank accounts directly</li>
            <li>We do not retain original PDF files after processing — only the extracted transaction data is stored</li>
          </ul>
        </section>

        <section>
          <h2 className="font-bold text-gray-900 text-base mb-3">3. How We Use Your Information</h2>
          <p>We use your information solely to provide the TaxBuddy service:</p>
          <ul className="list-disc pl-5 mt-2 space-y-1">
            <li>Processing and categorising transactions from uploaded statements</li>
            <li>Generating Excel reports for your accountant</li>
            <li>Storing your transaction history so it persists between sessions</li>
            <li>Sending transactional emails (password resets, account security)</li>
            <li>Improving the categorisation accuracy of the platform (aggregated, anonymised only)</li>
          </ul>
          <p className="mt-3">We do <strong>not</strong> use your financial data for advertising, profiling, or selling to third parties.</p>
        </section>

        <section>
          <h2 className="font-bold text-gray-900 text-base mb-3">4. Data Storage and Security</h2>
          <p>Your data is stored on servers located in Australia. We implement industry-standard security measures including:</p>
          <ul className="list-disc pl-5 mt-2 space-y-1">
            <li>AES-256 encryption at rest for all stored data</li>
            <li>TLS/HTTPS encryption in transit</li>
            <li>JWT-based authentication with 7-day token expiry</li>
            <li>No plain-text password storage (bcrypt hashing)</li>
            <li>Regular security reviews</li>
          </ul>
          <p className="mt-3">Despite our best efforts, no internet transmission is 100% secure. If you become aware of any security issue, please contact us immediately at <a href={`mailto:${EMAIL}`} className="text-blue-700 underline">{EMAIL}</a>.</p>
        </section>

        <section>
          <h2 className="font-bold text-gray-900 text-base mb-3">5. Data Sharing</h2>
          <p>We do not sell, rent, or trade your personal information. We may share data only in these limited circumstances:</p>
          <ul className="list-disc pl-5 mt-2 space-y-1">
            <li><strong>Service providers:</strong> We use Stripe for payment processing (subject to Stripe's privacy policy). Stripe does not receive your financial transaction data.</li>
            <li><strong>Legal requirements:</strong> We may disclose information if required by law, court order, or to protect the safety of users.</li>
            <li><strong>Business transfer:</strong> If TaxBuddy is acquired or merges, your data may be transferred. You will be notified 30 days in advance.</li>
          </ul>
        </section>

        <section>
          <h2 className="font-bold text-gray-900 text-base mb-3">6. Your Rights</h2>
          <p>Under Australian Privacy Principles and applicable law, you have the right to:</p>
          <ul className="list-disc pl-5 mt-2 space-y-1">
            <li><strong>Access</strong> the personal information we hold about you</li>
            <li><strong>Correct</strong> inaccurate or outdated information</li>
            <li><strong>Delete</strong> your account and all associated data (Settings → Delete Account)</li>
            <li><strong>Export</strong> your transaction data at any time via the Excel export feature</li>
            <li><strong>Opt out</strong> of non-essential communications</li>
          </ul>
          <p className="mt-3">To exercise these rights, contact us at <a href={`mailto:${EMAIL}`} className="text-blue-700 underline">{EMAIL}</a>. We will respond within 30 days.</p>
        </section>

        <section>
          <h2 className="font-bold text-gray-900 text-base mb-3">7. Data Retention</h2>
          <p>We retain your data for as long as your account is active. When you delete your account:</p>
          <ul className="list-disc pl-5 mt-2 space-y-1">
            <li>All transaction data is permanently deleted within 7 days</li>
            <li>Backups are purged within 30 days</li>
            <li>We retain minimal records required for legal compliance (billing records) for 7 years as required by Australian law</li>
          </ul>
        </section>

        <section>
          <h2 className="font-bold text-gray-900 text-base mb-3">8. Cookies</h2>
          <p>TaxBuddy uses only essential cookies required for authentication and security. We do not use advertising cookies, tracking pixels, or third-party analytics cookies that identify you personally.</p>
        </section>

        <section>
          <h2 className="font-bold text-gray-900 text-base mb-3">9. Children's Privacy</h2>
          <p>TaxBuddy is not directed at children under 18. We do not knowingly collect information from minors. If you believe a minor has provided us with personal information, please contact us.</p>
        </section>

        <section>
          <h2 className="font-bold text-gray-900 text-base mb-3">10. Changes to This Policy</h2>
          <p>We may update this Privacy Policy from time to time. We will notify you of significant changes by email and by displaying a prominent notice in the app at least 14 days before changes take effect.</p>
        </section>

        <section>
          <h2 className="font-bold text-gray-900 text-base mb-3">11. Complaints</h2>
          <p>If you have a complaint about how we handle your personal information, please contact us first at <a href={`mailto:${EMAIL}`} className="text-blue-700 underline">{EMAIL}</a>. If we cannot resolve your concern, you may lodge a complaint with the <a href="https://www.oaic.gov.au" target="_blank" rel="noopener noreferrer" className="text-blue-700 underline">Office of the Australian Information Commissioner (OAIC)</a>.</p>
        </section>
      </div>
    </div>
  );
}
